import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';

const quickLinks = [
  { label: 'About Us', path: '/en/about/core-advantages' },
  { label: 'ODM / OEM Partnership', path: '/en/partnership' },
  { label: 'Cooperation Process', path: '/en/cooperation/process' },
  { label: 'Services', path: '/en/services/hot-formulas' },
  { label: 'Contact Information', path: '/en/contact/info' },
];

const serviceItems = [
  { label: 'Popular Formulas', path: '/en/services/hot-formulas' },
  { label: 'Natural Ingredients', path: '/en/services/natural-ingredients' },
  { label: 'Dosage Forms', path: '/en/services/dosage-forms' },
  { label: 'Packaging Design', path: '/en/services/packaging' },
];

const contactInfo = [
  {
    icon: 'ri-map-pin-line',
    label: 'Address',
    value: '6F, No. 79, Sec. 1, Zhongqing Rd., North Dist., Taichung City, Taiwan',
    href: null,
  },
  {
    icon: 'ri-phone-line',
    label: 'Phone',
    value: '+886-4-2202-9858',
    href: 'tel:+886422029858',
  },
  {
    icon: 'ri-mail-line',
    label: 'Email',
    value: 'natural1070606@gmail.com',
    href: 'mailto:natural1070606@gmail.com',
  },
];

const certifications = [
  { label: 'GMP' },
  { label: 'HACCP' },
  { label: 'ISO 22000' },
  { label: 'SGS' },
];

interface EnLayoutProps {
  children: ReactNode;
}

export default function EnLayout({ children }: EnLayoutProps) {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>

      {/* English Footer */}
      <footer className="bg-[#1a2e1a] text-white">
        <div className="max-w-[1280px] mx-auto px-6 xl:px-10 py-16">
          <div className="footer-grid">
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/e260b0535e420cf4debd81c8e514ae3f.png"
                  alt="Kang Yong Biotech Logo"
                  className="h-10 w-auto object-contain brightness-0 invert"
                />
              </div>
              <h2 className="text-xl font-bold text-white mb-3">Kang Yong Biotech</h2>
              <p className="text-gray-300 text-[0.9375rem] leading-relaxed">
                Professional Health Supplement<br />
                OEM/ODM Manufacturing
              </p>
              <p className="text-gray-400 text-[0.875rem] leading-relaxed mt-4">
                From formula development to finished product delivery,<br />
                we provide end-to-end manufacturing solutions.
              </p>
            </div>

            {/* Column 2 - Quick Links */}
            <div>
              <h3 className="text-[1.125rem] font-semibold text-white mb-5">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-gray-300 text-[0.9375rem] hover:text-white transition-colors duration-200 flex items-center gap-2 group cursor-pointer"
                    >
                      <span className="w-0 h-0.5 bg-white transition-all duration-200 group-hover:w-3 flex-shrink-0" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 - Services */}
            <div>
              <h3 className="text-[1.125rem] font-semibold text-white mb-5">Our Services</h3>
              <ul className="space-y-3">
                {serviceItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-gray-300 text-[0.9375rem] hover:text-white transition-colors duration-200 flex items-center gap-2 group cursor-pointer"
                    >
                      <span className="w-0 h-0.5 bg-white transition-all duration-200 group-hover:w-3 flex-shrink-0" />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4 - Contact */}
            <div>
              <h3 className="text-[1.125rem] font-semibold text-white mb-5">Contact Information</h3>
              <ul className="space-y-4">
                {contactInfo.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[1.125rem] text-gray-300`} />
                    </div>
                    <div>
                      <span className="text-gray-400 text-[0.8125rem] block">{item.label}</span>
                      {item.href ? (
                        <a
                          href={item.href}
                          rel="nofollow"
                          className="text-gray-300 text-[0.875rem] hover:text-white transition-colors duration-200 cursor-pointer"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-gray-300 text-[0.875rem]">{item.value}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 5 - LINE */}
            <div className="min-w-0">
              <h3 className="text-[1.125rem] font-semibold text-white mb-5">LINE Official Account</h3>
              <div className="flex flex-col items-start gap-3">
                <a
                  href="https://lin.ee/X7Hcpf4"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex-shrink-0 cursor-pointer"
                  aria-label="Add LINE"
                >
                  <img
                    src="https://storage.readdy-site.link/project_files/4ba0dab9-dd17-40bb-9c01-736e9bd30d9b/caddd531-3b1d-4c43-9423-d53ab0804cd5_L_gainfriends_2dbarcodes_GW.png?v=7268c853782cd8d95252096de5ccf381"
                    alt="Kang Yong Biotech LINE QR Code"
                    className="w-[100px] h-[100px] object-contain rounded-lg bg-white p-1"
                  />
                </a>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-chat-smile-2-line text-[1rem] text-[#4ade80]" />
                    </div>
                    <span className="text-gray-300 text-[0.9375rem] font-medium">@126uwznp</span>
                  </div>
                  <p className="text-gray-400 text-[0.75rem] leading-relaxed mb-3">
                    Scan QR code to add LINE,<br className="hidden xl:block" />
                    inquire about OEM/ODM partnerships
                  </p>
                  <a
                    href="https://lin.ee/X7Hcpf4"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-2 bg-[#4ade80]/15 hover:bg-[#4ade80]/25 border border-[#4ade80]/40 text-[#4ade80] text-[0.8125rem] font-medium px-3 py-1.5 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-smile-2-line text-[0.9375rem]" />
                    Join LINE
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10">
          <div className="max-w-[1280px] mx-auto px-6 xl:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <p className="text-gray-400 text-[0.8125rem] text-center sm:text-left">
                &copy; {currentYear} Kang Yong Biotech Ltd.
              </p>
              <span className="hidden sm:inline text-gray-600 text-[0.75rem]">|</span>
              <Link
                to="/en/privacy-policy"
                className="text-gray-400 text-[0.8125rem] hover:text-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <span className="hidden sm:inline text-gray-600 text-[0.75rem]">|</span>
              <Link
                to="/en/terms-of-use"
                className="text-gray-400 text-[0.8125rem] hover:text-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Terms of Use
              </Link>
            </div>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              {certifications.map((c) => (
                <span key={c.label} className="text-gray-400 text-[0.75rem] border border-white/20 px-2.5 py-0.5 rounded-full">{c.label}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
